/**
 * Itinerary item wall-clock times — canonical storage is HH:mm (Device Sync).
 * Mirrors iOS NaiveWallClockDateParser: civil digits only, never device-local TZ.
 * Display / pickers use 12-hour AM/PM; sorting and comparisons parse both.
 */

import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatHm, hmFromUnknown } from './wallClock';

dayjs.extend(customParseFormat);

export const ITEM_TIME_PARSE_FORMATS = [
  'h:mm A',
  'h:mm a',
  'hh:mm A',
  'hh:mm a',
  'HH:mm',
  'H:mm',
] as const;

/**
 * Normalize any supported display/storage/ISO string to HH:mm wall-clock,
 * or undefined if invalid. ISO `…T09:30:00Z` keeps 09:30 (UTC civil digits).
 */
export function parseItemTimeToHHmm(raw: string | null | undefined): string | undefined {
  if (!raw?.trim()) return undefined;
  const trimmed = raw.trim();
  // 12-hour display strings must go through dayjs (hmFromUnknown would strip AM/PM).
  if (/[ap]\.?m\.?/i.test(trimmed)) {
    const parsed = dayjs(trimmed, [...ITEM_TIME_PARSE_FORMATS], true);
    return parsed.isValid() ? parsed.format('HH:mm') : undefined;
  }
  // ISO wall-clock or bare HH:mm — civil UTC digits, never local TZ conversion.
  if (trimmed.includes('T') || /^\d{1,2}:\d{2}/.test(trimmed)) {
    const wall = hmFromUnknown(trimmed);
    if (wall) return wall;
  }
  const parsed = dayjs(trimmed, [...ITEM_TIME_PARSE_FORMATS], true);
  return parsed.isValid() ? parsed.format('HH:mm') : undefined;
}

/** Minutes since midnight for chrono sort; null when missing/unparseable. */
export function itemTimeToMinutes(raw: string | null | undefined): number | null {
  const hm = parseItemTimeToHHmm(raw);
  if (!hm) return null;
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
}

/** Chronological compare; items without a valid time sort after timed items. */
export function compareItemTimes(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const ma = itemTimeToMinutes(a);
  const mb = itemTimeToMinutes(b);
  if (ma == null && mb == null) return 0;
  if (ma == null) return 1;
  if (mb == null) return -1;
  return ma - mb;
}

/** 12-hour display; empty string when no valid time. Uses UTC wall-clock digits. */
export function formatItemTimeDisplay(raw: string | null | undefined): string {
  const hm = parseItemTimeToHHmm(raw);
  if (!hm) return '';
  return formatHm(hm);
}

/** Dayjs for Ant TimePicker; null when missing/invalid. Civil HH:mm only. */
export function itemTimeToDayjs(raw: string | null | undefined): Dayjs | null {
  const hm = parseItemTimeToHHmm(raw);
  return hm ? dayjs(hm, 'HH:mm') : null;
}

/** Persist from TimePicker Dayjs (or legacy string) as HH:mm wall-clock. */
export function itemTimeFromFormValue(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (dayjs.isDayjs(value)) {
    // format() reads civil hour/minute from the picker value — do not use
    // toISOString() / local Date conversion (would shift near TZ boundaries).
    return value.isValid() ? value.format('HH:mm') : undefined;
  }
  if (typeof value === 'string') return parseItemTimeToHHmm(value);
  return undefined;
}
